import { translate } from '~l10n/client';

interface ApiError {
  response?: {
    status: number;
    data?: { error?: string; code?: string; message?: string; expiry?: number };
  };
  message?: string;
  isAxiosError?: boolean;
}

/**
 * Result type for insights error handling
 * - paywall: Show the paywall dialog (daily limit exceeded)
 * - toast: Show a toast notification with the provided message
 * - silent: Don't show anything (component will handle it)
 */
export type InsightsErrorResult =
  | { type: 'paywall' }
  | { type: 'toast'; message: string }
  | { type: 'silent' };

/**
 * Classifies errors from insights generation API calls
 * Returns an action to take (show paywall, show toast, or silent)
 * @param error The error from the API call
 * @returns InsightsErrorResult indicating what action to take
 */
export function handleInsightsError(error: ApiError): InsightsErrorResult {
  // Handle null/undefined errors
  if (!error || !error.response) {
    return {
      type: 'toast',
      message: translate('Unexpected Error'),
    };
  }
  
  // Check if it's a rate limit error (429 status)
  if (error.response?.status === 429) {
    const errorData = error.response.data;
    
    // Daily limit exceeded - show paywall (has expiry)
    if (errorData?.error === 'rate_limit_exceeded' && errorData?.expiry) {
      return { type: 'paywall' };
    }
    
    // Per-minute rate limit exceeded - show toast (no expiry)
    if (errorData?.error === 'rate_limit_exceeded' && !errorData?.expiry) {
      return {
        type: 'toast',
        message: translate('Rate Limit Exceeded. Wait a few moments and try again.'),
      };
    }
  }

  // Check for 400 Bad Request (insufficient data)
  if (error.response?.status === 400) {
    const errorData = error.response.data;
    
    if (errorData?.code === 'insufficient_data_error') {
      return { type: 'silent' };
    }
  }

  // Check for 503 Service Unavailable (capacity issues)
  if (error.response?.status === 503) {
    return {
      type: 'toast',
      message: translate('Beta feature is at capacity. Please try again later.'),
    };
  }

  // Generic error fallback
  return {
    type: 'toast',
    message: translate('Unexpected Error'),
  };
}
